import classNames from 'classnames';
import { useEffect, useRef, useState, type MouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * What the first version (v0.1.0) does not do yet, and what each part waits for. Every
 * part of the UI that depends on one shows faded, with an info mark at its top right
 * (`NotYetInfo`) that says why. Once one works, remove its key: the typecheck then points
 * at every fade to remove.
 */
export const NOT_YET = {
  ai: 'Conversation summaries and stage suggestions need an AI provider. They wait for its key (likely OpenAI) and for a decision on whether conversations may leave our infrastructure (Q10).',
  email: 'Nothing goes out by email yet. It waits for the tech team to choose an email provider and its sender address (Q22).',
  mailbox:
    'Prisma does not read contact@whitelabels.com yet. It waits for read access: an Entra ID application from whoever runs the Microsoft 365 tenant (Q23).',
  instagram:
    'Instagram is not connected. It waits for the sales team to name the account and to say whether Prisma should read and answer it; reading needs a Meta app review (Q18).',
  history: 'Not built yet: what shows here is made up. A later version lists the messages Prisma sent this owner and each status change the Owner Hub sync sees.',
  trigger:
    'Prisma keeps this trigger but does not act on its event yet: in this version only “A casino goes live” and “A lead is added” fire (Q14).',
} as const;

export type NotYetKey = keyof typeof NOT_YET;

/** The whole sentence, for a label or a title: "Not available yet. …". */
export const notYetText = (what: NotYetKey) => `Not available yet. ${NOT_YET[what]}`;

/** The fade, for an element that cannot be wrapped (a table cell, a tab's label). */
export const notYet = 'opacity-40';

const TIP_WIDTH = 288;

/** The tip, over everything (a portal), so no scrolling container clips it. */
const Tip = ({ what, at }: { what: NotYetKey; at: DOMRect }) => {
  const width = Math.min(TIP_WIDTH, window.innerWidth - 16);
  // Its right edge on the mark's (marks sit at a right edge), kept 8px inside the screen;
  // under the mark, or over it when the mark is low on the screen.
  const left = Math.min(Math.max(8, at.right - width), window.innerWidth - 8 - width);
  const place = at.bottom < window.innerHeight * 0.6 ? { top: at.bottom + 6 } : { bottom: window.innerHeight - at.top + 6 };
  return createPortal(
    <span
      aria-hidden
      style={{ left, width, ...place }}
      className="pointer-events-none fixed z-[70] rounded-md bg-surface-raised px-3 py-2 text-left text-xs leading-relaxed text-fg shadow-e4"
    >
      <span className="block font-medium text-fg-strong">Not available yet</span>
      <span className="mt-0.5 block text-fg-muted">{NOT_YET[what]}</span>
    </span>,
    document.body,
  );
};

/**
 * The info mark on a part that does not work in this version. Hovering it, focusing it or
 * tapping it shows that the part is not available yet and what it waits for, at once
 * (tooltips stay instant); a press elsewhere, Escape or a scroll closes it. A press on it
 * never does what its container does (follow a link, open a row, tick a checkbox).
 * `nested`: inside a link, a button or a label, where a second button is not allowed, so
 * the mark is not focusable there (the container's label carries the sentence instead).
 * `small`: 14px instead of 16px, beside a column header's small text.
 */
export const NotYetInfo = ({ what, nested, small, className }: { what: NotYetKey; nested?: boolean; small?: boolean; className?: string }) => {
  const ref = useRef<HTMLElement | null>(null);
  // How the last press came: "mouse", "touch", "pen", or "" for a key.
  const via = useRef('');
  const [at, setAt] = useState<DOMRect | null>(null);
  const show = () => ref.current && setAt(ref.current.getBoundingClientRect());

  useEffect(() => {
    if (!at) return;
    const hide = () => setAt(null);
    const away = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && hide();
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && hide();
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', escape);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [at]);

  const props = {
    ref: (el: HTMLElement | null) => {
      ref.current = el;
    },
    'aria-label': notYetText(what),
    onPointerEnter: (e: ReactPointerEvent) => e.pointerType === 'mouse' && show(),
    onPointerLeave: (e: ReactPointerEvent) => e.pointerType === 'mouse' && setAt(null),
    onPointerDown: (e: ReactPointerEvent) => {
      via.current = e.pointerType;
    },
    onClick: (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // A mouse keeps it shown (hover shows it already, unless the mark appeared under a
      // still pointer); a tap or a key toggles it.
      if (via.current === 'mouse' || !at) show();
      else setAt(null);
      via.current = '';
    },
    className: classNames(
      'pointer-events-auto inline-flex shrink-0 cursor-help rounded-full text-fg-muted transition-colors hover:text-fg-strong',
      className,
    ),
  };
  const mark = (
    <span className="relative flex">
      {/* A bigger target than the 16px mark: 44px on a phone (UI-1). */}
      <span className={classNames('absolute -inset-1', small ? 'max-md:-inset-[15px]' : 'max-md:-inset-[14px]')} aria-hidden />
      <svg viewBox="0 0 16 16" className={small ? 'h-3.5 w-3.5' : 'h-4 w-4'} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
        <circle cx="8" cy="8" r="6.25" />
        <path d="M8 7.25v3.75" />
        <path d="M8 4.9v.1" strokeWidth="2" />
      </svg>
    </span>
  );

  return (
    <>
      {nested ? (
        <span role="img" {...props}>
          {mark}
        </span>
      ) : (
        <button
          type="button"
          {...props}
          // A key's focus shows it; a press's focus does not, so the press itself decides.
          onFocus={() => !via.current && show()}
          onBlur={() => {
            // A press that never became a click (a scroll from the mark) must not hide a key's focus later.
            via.current = '';
            setAt(null);
          }}
          className={classNames(props.className, 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50')}
        >
          {mark}
        </button>
      )}
      {at && <Tip what={what} at={at} />}
    </>
  );
};

/**
 * Fades a block that does not work in this version, with the info mark at its top right.
 * `info`: where the mark sits inside the block, as Tailwind classes, or false when a mark
 * nearby already covers the block. `pad`: room for the mark on the right, when the block's
 * own content reaches its top right corner. A grid, so what it wraps still fills its place
 * (a tile in a row of tiles, a card).
 */
export const NotYet = ({
  what,
  info = 'right-4 top-4',
  pad,
  className,
  children,
}: {
  what: NotYetKey;
  info?: string | false;
  pad?: boolean;
  className?: string;
  children: ReactNode;
}) => (
  <div className={classNames('relative grid', className)}>
    <div className={classNames('grid', notYet, pad && 'pr-6')}>{children}</div>
    {info && <NotYetInfo what={what} className={classNames('absolute', info)} />}
  </div>
);
